import type { Aioha } from '@aioha/aioha';

const HIVESIGNER_CLIENT_ID = import.meta.env.VITE_HIVESIGNER_CLIENT_ID ?? 'hivett';
const HIVESIGNER_CALLBACK_PATH = '/auth/callback';
const HIVESIGNER_TOKEN_STORAGE_KEY = 'hivesignerToken';
const HIVESIGNER_EXPIRY_STORAGE_KEY = 'hivesignerExpiry';
const HIVESIGNER_USERNAME_STORAGE_KEY = 'hivesignerUsername';
const HIVESIGNER_TX_ID_STORAGE_KEY = 'hivesignerTxId';
// Legacy key that only held the expected username (no state). Cleared on use.
const HIVESIGNER_LEGACY_PENDING_USERNAME_STORAGE_KEY = 'hivett_hivesigner_expected_username';
// Kept in localStorage rather than sessionStorage because HiveSigner returns to
// the callback inside the popup window, a separate browsing context. Only this
// origin can read it, so a crafted link cannot know the expected state.
export const HIVESIGNER_PENDING_LOGIN_STORAGE_KEY = 'hivett_hivesigner_pending_login';
export const HIVESIGNER_PENDING_LOGIN_TTL_MS = 15 * 60_000;
export const HIVESIGNER_PAYMENT_NOTICE_STORAGE_KEY = 'hivett_hivesigner_payment_notice';

type AiohaModule = typeof import('@aioha/aioha');

export type WalletProvider = 'keychain' | 'hivesigner';
export type WalletAsset = 'HIVE' | 'HBD';
export type HiveSignerCallbackPayload = {
  accessToken?: string;
  expiresIn?: number;
  username?: string;
  transactionId?: string;
  error?: string;
  errorDescription?: string;
  state?: string;
};

export type PendingHiveSignerLogin = {
  state: string;
  username: string;
  createdAt: number;
};

export function createHiveSignerState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function constantTimeEquals(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let diff = 0;
  for (let index = 0; index < a.length; index += 1) {
    diff |= a.charCodeAt(index) ^ b.charCodeAt(index);
  }

  return diff === 0;
}

/**
 * Checks a HiveSigner login callback against the login this browser started.
 * Returns an error message, or null when the callback is genuine. Without
 * this, any link carrying someone else's access token would sign the victim
 * into that account (login CSRF).
 */
export function validateHiveSignerLoginCallback(
  payload: Pick<HiveSignerCallbackPayload, 'state' | 'username'>,
  pending: PendingHiveSignerLogin | null,
  now: number = Date.now()
): string | null {
  if (!pending) {
    return 'This sign-in link was not started from this browser. Start the HiveSigner sign-in again from Hivett.';
  }

  if (now - pending.createdAt > HIVESIGNER_PENDING_LOGIN_TTL_MS) {
    return 'The HiveSigner sign-in took too long and has expired. Please try again.';
  }

  if (!payload.state || !constantTimeEquals(payload.state, pending.state)) {
    return 'The HiveSigner sign-in could not be verified (state mismatch). Please try again.';
  }

  if (payload.username && payload.username !== pending.username) {
    return `HiveSigner signed in @${payload.username}, but @${pending.username} was requested. Please try again.`;
  }

  return null;
}

export function getDefaultHiveSignerCallbackUrl(origin: string): string {
  return `${origin}${HIVESIGNER_CALLBACK_PATH}`;
}

export function getHiveSignerCallbackUrl(origin: string, configured?: string): string {
  const fallback = getDefaultHiveSignerCallbackUrl(origin);
  const trimmed = configured?.trim();

  if (!trimmed) {
    return fallback;
  }

  if (trimmed.startsWith('/')) {
    return new URL(trimmed, origin).toString();
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return fallback;
  }

  try {
    const url = new URL(trimmed);
    return url.origin === origin ? url.toString() : fallback;
  } catch {
    return fallback;
  }
}

function getCallbackParams(href: string): URLSearchParams {
  const url = new URL(href);
  const params = new URLSearchParams(url.search);
  const hashInput = url.hash.startsWith('#')
    ? url.hash.slice(1)
    : url.hash;
  const hashParams = new URLSearchParams(hashInput);

  for (const [key, value] of hashParams.entries()) {
    if (!params.has(key)) {
      params.set(key, value);
    }
  }

  return params;
}

export function getHiveSignerCallbackPayload(href: string): HiveSignerCallbackPayload | null {
  const params = getCallbackParams(href);
  const accessToken = params.get('access_token')?.trim() || undefined;
  const transactionId = params.get('id')?.trim() || undefined;
  const error = params.get('error')?.trim() || undefined;
  const errorDescription = params.get('error_description')?.trim() || undefined;
  const state = params.get('state')?.trim() || undefined;
  const username = params.get('username')?.trim().toLowerCase() || undefined;
  const expiresRaw = params.get('expires_in')?.trim();
  const expiresIn = expiresRaw ? Number.parseInt(expiresRaw, 10) : undefined;
  const parsedExpiresIn = typeof expiresIn === 'number' && Number.isFinite(expiresIn)
    ? expiresIn
    : null;

  if (!accessToken && !transactionId && !error && !errorDescription && !state) {
    return null;
  }

  const payload: HiveSignerCallbackPayload = {};

  if (accessToken) {
    payload.accessToken = accessToken;
  }

  if (parsedExpiresIn !== null) {
    payload.expiresIn = parsedExpiresIn;
  }

  if (username) {
    payload.username = username;
  }

  if (transactionId) {
    payload.transactionId = transactionId;
  }

  if (error) {
    payload.error = error;
  }

  if (errorDescription) {
    payload.errorDescription = errorDescription;
  }

  if (state) {
    payload.state = state;
  }

  return payload;
}

export class AiohaService {
  private modulePromise: Promise<AiohaModule> | null = null;
  private aiohaPromise: Promise<Aioha> | null = null;
  private aioha: Aioha | null = null;
  private providerAvailability: Record<WalletProvider, boolean> = {
    keychain: false,
    hivesigner: false
  };

  public get isKeychainAvailable(): boolean {
    return this.providerAvailability.keychain;
  }

  public get isHiveSignerAvailable(): boolean {
    return this.providerAvailability.hivesigner;
  }

  public async ensureReady() {
    await this.getAioha();
  }

  public async isProviderEnabled(provider: WalletProvider): Promise<boolean> {
    await this.getAioha();
    return this.providerAvailability[provider];
  }

  public async getCurrentProvider(): Promise<WalletProvider | undefined> {
    if (this.aioha === null) {
      return undefined;
    }

    const { Providers } = await this.getModule();
    const provider = this.aioha.getCurrentProvider();
    if (provider === Providers.Keychain) {
      return 'keychain';
    }
    if (provider === Providers.HiveSigner) {
      return 'hivesigner';
    }
    return undefined;
  }

  public isLoggedIn(): boolean {
    return this.aioha?.isLoggedIn() ?? false;
  }

  public async logout() {
    this.clearHiveSignerSession();

    if (this.aioha === null) {
      return;
    }

    await this.aioha.logout();
  }

  private clearHiveSignerSession() {
    localStorage.removeItem(HIVESIGNER_TOKEN_STORAGE_KEY);
    localStorage.removeItem(HIVESIGNER_EXPIRY_STORAGE_KEY);
    localStorage.removeItem(HIVESIGNER_USERNAME_STORAGE_KEY);
    localStorage.removeItem(HIVESIGNER_TX_ID_STORAGE_KEY);
    this.clearPendingHiveSignerUsername();
  }

  public async login(provider: WalletProvider, account: string, challengeMessage?: string) {
    const aioha = await this.getAioha();
    const { KeyTypes, Providers } = await this.getModule();

    if (provider === 'hivesigner') {
      // Aioha reads the token from localStorage once the popup closes, so
      // drop anything left over from an earlier login before starting.
      localStorage.removeItem(HIVESIGNER_TOKEN_STORAGE_KEY);
      localStorage.removeItem(HIVESIGNER_EXPIRY_STORAGE_KEY);
      localStorage.removeItem(HIVESIGNER_USERNAME_STORAGE_KEY);

      const state = createHiveSignerState();
      this.setPendingHiveSignerLogin({
        state,
        username: account.trim().toLowerCase(),
        createdAt: Date.now()
      });

      const result = await aioha.login(Providers.HiveSigner, account, {
        keyType: KeyTypes.Posting,
        hivesigner: { state }
      });

      if (!result.success) {
        this.clearPendingHiveSignerUsername();
      }

      return result;
    }

    const loginOptions: {
      keyType: typeof KeyTypes.Posting;
      loginTitle: string;
      msg?: string;
    } = {
      keyType: KeyTypes.Posting,
      loginTitle: 'hivett authentication'
    };

    if (challengeMessage) {
      loginOptions.msg = challengeMessage;
    }

    return aioha.login(Providers.Keychain, account, loginOptions);
  }

  public async transfer(to: string, amount: number, currency: WalletAsset, memo: string) {
    const aioha = await this.getAioha();
    const { Asset } = await this.getModule();

    return aioha.transfer(to, amount, currency === 'HIVE' ? Asset.HIVE : Asset.HBD, memo);
  }

  public consumeHiveSignerCallback(href: string): HiveSignerCallbackPayload | null {
    const payload = getHiveSignerCallbackPayload(href);

    if (!payload) {
      return null;
    }

    if (payload.transactionId) {
      localStorage.setItem(HIVESIGNER_TX_ID_STORAGE_KEY, payload.transactionId);
    }

    if (payload.accessToken) {
      const pending = this.getPendingHiveSignerLogin();
      // One attempt per state: clear it whether or not it matches.
      this.clearPendingHiveSignerUsername();
      const stateError = validateHiveSignerLoginCallback(payload, pending);

      if (stateError) {
        // Never store or forward a token we did not ask for.
        return {
          error: 'invalid_state',
          errorDescription: stateError
        };
      }

      localStorage.setItem(HIVESIGNER_TOKEN_STORAGE_KEY, payload.accessToken);

      if (typeof payload.expiresIn === 'number' && payload.expiresIn > 0) {
        localStorage.setItem(
          HIVESIGNER_EXPIRY_STORAGE_KEY,
          Math.floor(Date.now() / 1000 + payload.expiresIn).toString()
        );
      }

      const username = payload.username ?? pending?.username;
      if (username) {
        localStorage.setItem(HIVESIGNER_USERNAME_STORAGE_KEY, username);
        payload.username = username;
      }
    }

    if (payload.accessToken || payload.error) {
      this.clearPendingHiveSignerUsername();
    }

    return payload;
  }

  public clearPendingHiveSignerUsername() {
    localStorage.removeItem(HIVESIGNER_PENDING_LOGIN_STORAGE_KEY);
    localStorage.removeItem(HIVESIGNER_LEGACY_PENDING_USERNAME_STORAGE_KEY);
  }

  private async getModule(): Promise<AiohaModule> {
    if (!this.modulePromise) {
      // Do not cache a failed chunk load (offline, deploy in progress): the
      // next call should try the import again.
      this.modulePromise = import('@aioha/aioha').catch((error: unknown) => {
        this.modulePromise = null;
        this.aiohaPromise = null;
        throw error;
      });
    }

    return this.modulePromise;
  }

  private async getAioha(): Promise<Aioha> {
    if (this.aioha !== null) {
      return this.aioha;
    }

    if (!this.aiohaPromise) {
      this.aiohaPromise = this.createAioha().catch((error: unknown) => {
        this.aiohaPromise = null;
        throw error;
      });
    }

    this.aioha = await this.aiohaPromise;
    return this.aioha;
  }

  private async createAioha(): Promise<Aioha> {
    const { Providers, initAioha } = await this.getModule();
    const aioha = initAioha();
    const callbackURL = getHiveSignerCallbackUrl(
      window.location.origin,
      import.meta.env.VITE_HIVESIGNER_REDIRECT_URI
    );

    aioha.setup({
      hivesigner: {
        app: HIVESIGNER_CLIENT_ID,
        callbackURL,
        scope: ['login']
      }
    });

    this.providerAvailability = {
      keychain: aioha.isProviderEnabled(Providers.Keychain),
      hivesigner: aioha.isProviderEnabled(Providers.HiveSigner)
    };

    return aioha;
  }

  private getPendingHiveSignerLogin(): PendingHiveSignerLogin | null {
    const raw = localStorage.getItem(HIVESIGNER_PENDING_LOGIN_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<PendingHiveSignerLogin> | null;
      if (
        !parsed
        || typeof parsed.state !== 'string'
        || !parsed.state
        || typeof parsed.username !== 'string'
        || typeof parsed.createdAt !== 'number'
      ) {
        return null;
      }

      return { state: parsed.state, username: parsed.username, createdAt: parsed.createdAt };
    } catch {
      return null;
    }
  }

  private setPendingHiveSignerLogin(pending: PendingHiveSignerLogin) {
    localStorage.removeItem(HIVESIGNER_LEGACY_PENDING_USERNAME_STORAGE_KEY);
    localStorage.setItem(HIVESIGNER_PENDING_LOGIN_STORAGE_KEY, JSON.stringify(pending));
  }
}
